// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ArrowForward } from '@/constants/hardfin-standard-icons';
import { Workflow } from '@/workflow/api/types';
import { useWorkflows } from '@/workflow/hooks/use-workflows';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

interface WorkflowSelectorProps {
  /**
   * The currently selected workflow ID
   */
  value: string | null;

  /**
   * Callback to update the selected workflow ID
   */
  onChange: (workflowId: string | null) => void;

  /**
   * The label for the workflow selector
   */
  label: string;

  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;
}

function WorkflowSelectorContent(props: WorkflowSelectorProps) {
  const { data: workflows = [], isLoading, error } = useWorkflows();

  // Find the currently selected workflow object
  const selectedWorkflow =
    workflows.find((workflow) => workflow.id === props.value) || null;

  const handleChange = (_: React.SyntheticEvent, newValue: Workflow | null) => {
    props.onChange(newValue?.id || null);
  };

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load workflows
      </Typography>
    );
  }

  return (
    <Box>
      <Autocomplete
        options={workflows}
        getOptionLabel={(option) => option.name}
        value={selectedWorkflow}
        onChange={handleChange}
        loading={isLoading}
        disabled={props.disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps}>
              {option.name}
            </Box>
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        clearOnBlur
        handleHomeEndKeys
      />

      {selectedWorkflow && selectedWorkflow.stages.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            py: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {selectedWorkflow.stages
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((stage, index) => (
                <Box
                  key={stage.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <span>{stage.name}</span>
                  {index < selectedWorkflow.stages.length - 1 && (
                    <ArrowForward
                      fontSize="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              ))}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * A workflow selector component that allows users to choose from available workflows.
 *
 * This component is automatically hidden when the enableProjectWorkflows feature flag
 * is disabled. When enabled, it displays an autocomplete dropdown of available workflows
 * fetched from the useWorkflows hook, matching the styling of other form components.
 */
export function WorkflowSelector(props: WorkflowSelectorProps) {
  return <WorkflowSelectorContent {...props} />;
}
